import { Component, Fragment } from "react"
import { isEmpty, get, map } from "lodash"
import { Link } from "react-router-dom"
import PropTypes from "prop-types"
import classNames from "classnames"
import { IconButton, Menu, MenuItem, Chip, Popover, Divider } from "@mui/material"
import { Error, MoreVert, Exposure, BlurLinear, Info, AspectRatio, BrokenImage, OfflineBolt, MoneyOff } from "@mui/icons-material"
import { getSegmentNormalization, NORMALIZATION } from "helpers/segments"
import OPTooltip from "components/material/tooltip"
import "./style.scss"

function renderInfo(item, i) {
  if (item.values) {
    return <Fragment key={i}>
      <li className="item-info-label">{item.label}</li>
      {map(item.values, (item, j) => item
        && <li key={j} className={item.className}>
          <div className="label">{item.label}</div>
          <div className="value">{item.value || <i>No {item.label} provided</i>}</div>
        </li>,
      )}
    </Fragment>
  } else if (item.value) {
    return <Fragment key={i}>
      <li className="item-info-label">{item.label}</li>
      <li className="item-info-value">{item.value || <i>No {item.label} provided</i>}</li>
    </Fragment>
  }

  return <li key={i} className="label">{item.label}</li>
}

class ComparisonItem extends Component {
  static propTypes = {
    item: PropTypes.object.isRequired,
    removeEnabled: PropTypes.bool.isRequired,
    className: PropTypes.string.isRequired,
    dispatch: PropTypes.func.isRequired,
    openDialog: PropTypes.func.isRequired,
  }

  constructor(...args) {
    super(...args)

    this.state = {
      menuAnchor: null,
      popoverAnchor: null,
    }
  }

  action = (action, item, confirmationDialog) => {
    const { dispatch, openDialog } = this.props
    if (confirmationDialog) {
      return () => {
        openDialog({
          title: confirmationDialog.title,
          contentText: confirmationDialog.content,
          buttons: [{
            text: confirmationDialog.buttonText,
            type: "primary",
            action: () => {
              this.toggleMenu()
              dispatch(action(item))
            },
          }],
        })
      }
    }

    return () => {
      this.toggleMenu()
      dispatch(action(item))
    }
  }

  handleOpenPopover = () => {
    return (event) => {
      this.setState({ popoverAnchor: event.currentTarget })
    }
  }

  handleClosePopover = () => {
    this.setState({ popoverAnchor: null })
  }

  toggleMenu = ev => {
    const { menuAnchor } = this.state
    if (menuAnchor) {
      this.setState({ menuAnchor: null })
    } else {
      this.setState({ menuAnchor: ev.currentTarget })
    }
  }

  render() {
    const { item, removeEnabled, className } = this.props
    const { menuAnchor, popoverAnchor } = this.state
    const itemType = item.type === "swap" ? "basket" : item.type
    const info = item.info || []
    const tooltipClasses = { popper: "compare-tooltip-info", tooltip: "compare-tooltip" }

    return <div data-test-class="comparison-item" className={classNames("comparison-item", className)}>
      <div className="compare-item-background" style={{ background: item.color }} />
      <div className="status-icons">
        {item.noData
          && <OPTooltip
            title={`No data was found for this ${itemType}, most likely due to an incomplete upload. Please upload new data or delete this experiment.`}
            classes={tooltipClasses}
          >
            <Error className="compare-icons" />
          </OPTooltip>}
        {removeEnabled && item.isUnmodeled && !item.noData
          && <OPTooltip
            title={`No data was found for this ${itemType} with the selected risk model. Consider switching the risk model or removing this comparison.`}
            classes={tooltipClasses}
          >
            <Error className="compare-icons" />
          </OPTooltip>}
        {!item.noData && !item.isUnmodeled && item.evaluateAs === "short"
          && <OPTooltip
            title="Evaluated as a short position"
            classes={tooltipClasses}
          >
            <Exposure className="compare-icons" />
          </OPTooltip>}
        {item.hedge && item.evaluateAs !== "short" && <OPTooltip
          title={`Hedge option \n${get(item, "hedge.instrument.ticker", "")} @ ${get(item, "hedge.size")}%`}
          classes={tooltipClasses}
        >
          <BlurLinear className="compare-icons" />
        </OPTooltip>}
        {item.riskAdjusted
          && <OPTooltip title="Normalization: Contribution is not supported for the time-series chart when in active mode." classes={tooltipClasses}>
            <OfflineBolt className="compare-icons primary" />
          </OPTooltip>}
        {!item.noData && !item.isUnmodeled && getSegmentNormalization(item) === NORMALIZATION.STANDALONE && <OPTooltip
          title="Normalized as standalone"
          classes={tooltipClasses}
        >
          <AspectRatio className="compare-icons" />
        </OPTooltip>}
        {!item.noData && !item.isUnmodeled && getSegmentNormalization(item) === NORMALIZATION.CONTRIBUTION && <OPTooltip
          title="Normalized as contributor"
          classes={tooltipClasses}
        >
          <BrokenImage className="compare-icons" />
        </OPTooltip>}
        {item.fxHedgeEnabled && <OPTooltip
          title="FX hedge enabled"
          classes={tooltipClasses}
        >
          <MoneyOff className="compare-icons" />
        </OPTooltip>}
      </div>
      <div className="comparison-item-wrapper">
        <OPTooltip title={item.label}>
          <Chip
            icon={item.icon}
            label={item.label}
            clickable={!isEmpty(item.info)}
            className="comparison-item-label"
            onClick={this.handleOpenPopover(item.type)}
          />
        </OPTooltip>

        {!isEmpty(item.actions)
        && <>
          <IconButton onClick={this.toggleMenu} data-test-class="comparison-item-menu-button" className="comparison-item-menu" size="large">
            <MoreVert />
          </IconButton>
          <Menu
            anchorOrigin={{ horizontal: "right", vertical: "top" }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={this.toggleMenu}
            data-test-class="comparison-item-menu-popover"
          >
            {map(item.actions, (action, i) => {
              if (action.link) { return (<MenuItem key={i}><Link to={action.link}>{action.label}</Link></MenuItem>) }
              let menu = <MenuItem key={i} data-test-class="comparison-item-menu-option" disabled={action.disabled} onClick={this.action(action.fn, item, action.confirmationDialog)}>
                {action.label}
              </MenuItem>

                if (action.disabled && action.disabledTooltip) {
                  menu = <OPTooltip
                    key={`tooltip-${i}`}
                    title={action.disabledTooltip}
                    classes={tooltipClasses}
                  >
                    <div className="disabled-menuItem-tooltip">
                      {menu}
                      <Info />
                    </div>
                  </OPTooltip>
                }

                if (i > 0 && get(action, "group") !== get(item.actions[i - 1], "group")) {
                  return [<Divider key={`divider-${i}`} orientation="horizontal" />, menu]
                }
                return menu
              },
              )}
          </Menu>
        </>}
      </div>
      <Popover
        className="info-popover"
        anchorEl={popoverAnchor}
        open={!!popoverAnchor}
        onClose={this.handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <div className="compare-item-info-wrapper">
          <ul className="compare-item-info">
            <li>
              <div className="label">Name</div>
              <div className="value">{item.label}</div>
            </li>
            {info && map(info, renderInfo)}
          </ul>
        </div>
      </Popover>
    </div>
  }
}

export default ComparisonItem
